/**
 * Wires the app lock (idle-lock.ts) to its device signals and the per-device policy, and exposes
 * a status snapshot for You › Security ("Lock Viko when…", with the live signal source).
 */
import type { MachinePresenceFrame } from '@viko/protocol'
import {
  type AppLockRecord,
  DEFAULT_LOCK_POLICY,
  IdleLock,
  type LockPolicy,
  type LockSignal,
  type LockSource,
  loadAppLock,
  loadLockPolicy,
  saveLockPolicy,
} from './idle-lock.js'
import {
  type IdlePermission,
  type IdlePromptAnswer,
  idlePermission,
  loadIdlePrompt,
  loadLocalMachine,
  requestIdlePermission,
  saveIdlePrompt,
  saveLocalMachine,
  startIdleDetector,
  watchPresence,
} from './lock-signals.js'

export interface AppLockStatus {
  locked: boolean
  mobile: boolean
  policy: LockPolicy
  source: LockSource
  idlePermission: IdlePermission
  /** Show the one-time, explained Idle Detection prompt. */
  promptIdle: boolean
  localMachine?: string
  /** Last presence report from the local vikod, as a lock signal. */
  presence?: LockSignal
  /** passkey-budget WP1: the last time the OS login (not a passkey) unlocked Viko. */
  presenceUnlockedAt?: number
}

export interface AppLockDeps {
  verify: () => Promise<void>
  /**
   * passkey-budget WP1 (lock follows the OS): verify a daemon-signed proof that the OS screen
   * locked at or after `since` and was unlocked since. Desktop only; false keeps the lock.
   */
  presenceUnlock?: (since: number) => Promise<boolean>
  persist?: (record: AppLockRecord) => Promise<void>
  mobile: boolean
  now?: () => number
  readPresence: (machineId: string) => Promise<MachinePresenceFrame | undefined>
  onWork: (cb: (f: { entity: string; id: string }) => void) => () => void
  load?: {
    record?: () => Promise<AppLockRecord | undefined>
    policy?: () => Promise<LockPolicy>
    localMachine?: () => Promise<string | undefined>
    idlePrompt?: () => Promise<IdlePromptAnswer | undefined>
  }
  idle?: {
    permission?: () => Promise<IdlePermission>
    request?: () => Promise<IdlePermission>
    start?: typeof startIdleDetector
  }
}

export class AppLockController {
  readonly lock: IdleLock
  private status: AppLockStatus
  private readonly listeners = new Set<() => void>()
  private stopIdle: AbortController | undefined
  private stopPresence: (() => void) | undefined
  private presenceTry: Promise<boolean> | undefined
  constructor(private readonly d: AppLockDeps) {
    this.lock = new IdleLock({
      verify: d.verify,
      mobile: d.mobile,
      ...(d.persist ? { persist: d.persist } : {}),
      ...(d.now ? { now: d.now } : {}),
    })
    this.status = {
      locked: false,
      mobile: d.mobile,
      policy: DEFAULT_LOCK_POLICY,
      source: this.lock.source(),
      idlePermission: 'unsupported',
      promptIdle: false,
    }
    this.lock.subscribe(() => this.update({}))
  }
  snapshot = () => this.status
  subscribe = (fn: () => void) => {
    this.listeners.add(fn)
    return () => void this.listeners.delete(fn)
  }
  private update(patch: Partial<AppLockStatus>) {
    this.status = {
      ...this.status,
      ...patch,
      locked: this.lock.locked,
      source: this.lock.source(),
    }
    for (const fn of this.listeners) fn()
  }
  /** Restore the persisted lock first, then start signals. Returns a disposer. */
  async start(): Promise<() => void> {
    const l = this.d.load ?? {}
    this.lock.restore(await (l.record ?? loadAppLock)().catch(() => undefined))
    const policy = await (l.policy ?? loadLockPolicy)()
    this.lock.setPolicy(policy)
    this.update({ policy })
    if (!this.d.mobile) {
      const machine = await (l.localMachine ?? loadLocalMachine)()
      this.watch(machine)
      const permission = await (this.d.idle?.permission ?? idlePermission)()
      const answered = await (l.idlePrompt ?? loadIdlePrompt)()
      this.update({
        idlePermission: permission,
        promptIdle: permission === 'prompt' && !answered && policy.desktop === 'device',
      })
      if (permission === 'granted') await this.startIdle()
    }
    if (this.lock.locked) void this.tryPresenceUnlock()
    return () => this.stop()
  }
  /**
   * passkey-budget WP1: clear the app lock with the OS login when this computer's vikod can prove
   * it (one request at a time). Mobile never uses it (WP2 handles the phone).
   */
  tryPresenceUnlock(): Promise<boolean> {
    const check = this.d.presenceUnlock
    if (this.d.mobile || !check || !this.lock.locked) return Promise.resolve(false)
    this.presenceTry ??= this.lock
      .unlockWith(check)
      .then((ok) => {
        if (ok) this.update({ presenceUnlockedAt: (this.d.now ?? Date.now)() })
        return ok
      })
      .finally(() => {
        this.presenceTry = undefined
      })
    return this.presenceTry
  }
  stop() {
    this.stopIdle?.abort()
    this.stopPresence?.()
    this.stopIdle = this.stopPresence = undefined
  }
  private signal = (source: 'idle-detector' | 'daemon') => (s: LockSignal) => {
    this.lock.signal(source, s)
    this.update(source === 'daemon' ? { presence: s } : {})
    // passkey-budget WP1: the OS screen is unlocked again → ask vikod for the signed proof.
    if (source === 'daemon' && s.screen === 'unlocked' && this.lock.locked)
      void this.tryPresenceUnlock()
  }
  private async startIdle() {
    this.stopIdle?.abort()
    const abort = new AbortController()
    this.stopIdle = abort
    const start = this.d.idle?.start ?? startIdleDetector
    await start(this.signal('idle-detector'), {
      signal: abort.signal,
      ...(this.d.now ? { now: this.d.now } : {}),
    })
    this.update({})
  }
  private watch(machineId: string | undefined) {
    this.stopPresence?.()
    this.stopPresence = undefined
    const { presence: _p, localMachine: _m, ...rest } = this.status
    this.status = rest
    if (!machineId) return this.update({})
    this.stopPresence = watchPresence({
      machineId,
      read: this.d.readPresence,
      onWork: this.d.onWork,
      onSignal: this.signal('daemon'),
      ...(this.d.now ? { now: this.d.now } : {}),
    })
    this.update({ localMachine: machineId })
  }
  /** From a user gesture: the one-time prompt's "Allow", or You › Security. */
  async allowIdleDetection() {
    const permission = await (this.d.idle?.request ?? requestIdlePermission)()
    await saveIdlePrompt(permission === 'granted' ? 'granted' : 'denied').catch(() => {})
    this.update({ idlePermission: permission, promptIdle: false })
    if (permission === 'granted') await this.startIdle()
  }
  async dismissIdlePrompt() {
    await saveIdlePrompt('dismissed').catch(() => {})
    this.update({ promptIdle: false })
  }
  async setPolicy(policy: LockPolicy) {
    this.lock.setPolicy(policy)
    this.update({ policy })
    await saveLockPolicy(policy)
  }
  async setLocalMachine(machineId: string | undefined) {
    await saveLocalMachine(machineId)
    if (!this.d.mobile) this.watch(machineId)
  }
}

let current: AppLockController | undefined
const registry = new Set<() => void>()
/** The mounted app lock (one per page), for settings and enrollment. */
export const appLock = {
  get: () => current,
  set(c: AppLockController | undefined) {
    current = c
    for (const fn of registry) fn()
  },
  subscribe(fn: () => void) {
    registry.add(fn)
    return () => void registry.delete(fn)
  },
}
